import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCompass, FaBars, FaSpinner, FaFileAlt, FaSave, FaSearch, FaExternalLinkAlt, FaDatabase, FaDownload } from 'react-icons/fa';
import jsPDF from 'jspdf';
import { generateCompanyInfo } from '../../../services/openai';
import { generateDocument, saveCompanyToDatabase, searchCompanies, checkCompanyExists } from '../../../services/openai';
import './CompassEditor.css';
import Compass from './Compass';
import { useUsage } from '../../../hooks/useUsage';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';

const DOCUMENT_TYPES = {
  ACCOUNT_BRIEF: 'Account Brief',
  BATTLE_CARD: 'Battle Card',
  OBJECTION_HANDLING: 'Objection Handling',
  PROPOSAL: 'Proposal'
};

const DOCUMENT_FIELDS = {
  [DOCUMENT_TYPES.ACCOUNT_BRIEF]: [
    { 
      name: 'relationshipStatus', 
      label: 'Current Client Relationship Status', 
      type: 'select', 
      required: true,
      options: ['New Prospect', 'Existing Client', 'Former Client']
    },
    { 
      name: 'dealStage', 
      label: 'Deal Stage', 
      type: 'select', 
      required: false,
      options: ['Prospecting', 'Qualified Lead', 'Proposal Stage', 'Negotiation', 'Closed Won', 'Closed Lost']
    },
    { 
      name: 'engagementGoals', 
      label: 'Engagement Goals', 
      type: 'checkbox-group', 
      required: true,
      options: [
        'Initial outreach preparation',
        'Meeting preparation',
        'Call follow-up research',
        'Proposal development support'
      ]
    },
    { 
      name: 'keyPainPoints', 
      label: 'Key Pain Points to Focus On', 
      type: 'textarea', 
      required: true 
    },
    { 
      name: 'targetProducts', 
      label: 'Specific Products/Services Being Targeted', 
      type: 'textarea', 
      required: true 
    },
    { 
      name: 'competitors', 
      label: 'Competitors Being Considered', 
      type: 'textarea', 
      required: false 
    }
  ],
  [DOCUMENT_TYPES.BATTLE_CARD]: [
    {
      name: 'competitors',
      label: 'Competitor Names & Types',
      type: 'multi-field',
      required: true,
      fields: [
        { name: 'name', label: 'Competitor Name', type: 'text' },
        { name: 'type', label: 'Type', type: 'select', options: ['Direct', 'Indirect', 'Legacy'] }
      ]
    },
    {
      name: 'productLine',
      label: 'Specific Product/Service Line',
      type: 'textarea',
      required: false
    },
    {
      name: 'marketSegment',
      label: 'Market Segment Focus',
      type: 'select',
      required: true,
      options: ['Enterprise', 'Mid-Market', 'SMB']
    },
    {
      name: 'competitiveSituations',
      label: 'Competitive Situations',
      type: 'checkbox-group',
      required: true,
      options: [
        'Head-to-head evaluation',
        'Displacement opportunity (replacing competitor)',
        'Competitive trap (brought in late to compare)',
        '"No decision" competitor (status quo)'
      ]
    },
    {
      name: 'buyerPersonas',
      label: 'Buyer Personas Typically Involved',
      type: 'checkbox-group',
      required: true,
      options: ['Technical', 'Financial', 'Executive']
    },
    {
      name: 'customerPainPoints',
      label: 'Common Customer Pain Points',
      type: 'textarea',
      required: false
    },
    {
      name: 'informationNeeded',
      label: 'Areas Where More Information is Needed',
      type: 'checkbox-group',
      required: true,
      options: [
        'Feature comparisons',
        'Pricing differentiators',
        'Customer testimonials',
        'Technical limitations',
        'Implementation differences'
      ]
    }
  ],
  [DOCUMENT_TYPES.OBJECTION_HANDLING]: [
    {
      name: 'objectionCategory',
      label: 'Objection Category',
      type: 'select',
      required: true,
      options: [
        'Price/Budget concerns',
        'Need/Value concerns',
        'Timing/Urgency concerns',
        'Product/Feature concerns',
        'Technical/Integration concerns',
        'Competitive concerns',
        'Internal process/Decision-making concerns',
        'Risk/Security concerns',
        'Support/Service concerns',
        'Custom/Other'
      ]
    },
    {
      name: 'buyerPersona',
      label: 'Buyer Persona & PrimaryMotivation',
      type: 'multi-field',
      required: true,
      fields: [
        { name: 'role', label: 'Role/Title', type: 'text', placeholder: 'e.g., CTO, CFO, end-user' },
        { name: 'motivation', label: 'Primary Motivation', type: 'text', placeholder: 'e.g., ROI, technical excellence, ease of use' }
      ]
    },
    {
      name: 'dealStage',
      label: 'Deal Stage',
      type: 'select',
      required: true,
      options: [
        'Early Discovery',
        'Mid-Evaluation',
        'Late Negotiation'
      ]
    },
    {
      name: 'productsServices',
      label: 'Products/Services Being Discussed',
      type: 'textarea',
      required: true,
      placeholder: 'List the specific products or services being discussed in this deal'
    },
    {
      name: 'previousInteractions',
      label: 'Previous Interactions Summary',
      type: 'textarea',
      required: false,
      placeholder: 'Summarize any relevant previous interactions with the prospect'
    },
    {
      name: 'hotButtons',
      label: 'Known Hot Buttons or Priorities',
      type: 'textarea',
      required: false,
      placeholder: 'List any known priorities or hot buttons for this prospect'
    },
    {
      name: 'communicationChannel',
      label: 'Communication Channel',
      type: 'select',
      required: true,
      options: [
        'Live Meeting',
        'Email',
        'Phone Call'
      ]
    },
    {
      name: 'tonePreference',
      label: 'Tone Preference',
      type: 'select',
      required: true,
      options: [
        'Consultative',
        'Direct',
        'Educational',
        'Empathetic'
      ]
    },
    {
      name: 'responseLength',
      label: 'Response Length',
      type: 'select',
      required: true,
      options: [
        'Quick Bullet Points',
        'Detailed Response'
      ]
    }
  ],
  [DOCUMENT_TYPES.PROPOSAL]: [
    {
      name: 'proposalType',
      label: 'Proposal Type',
      type: 'select',
      required: true,
      options: [
        'Initial Proposal',
        'Revised Proposal',
        'Final Proposal',
        'Statement of Work (SOW)',
        'Request for Proposal (RFP) Response',
        'Custom Proposal'
      ]
    },
    {
      name: 'dealStage',
      label: 'Deal Stage',
      type: 'select',
      required: true,
      options: [
        'Proposal Development',
        'Proposal Review',
        'Negotiation',
        'Final Approval',
        'Contract Signing'
      ]
    },
    {
      name: 'clientNeeds',
      label: 'Client Needs & Requirements',
      type: 'textarea',
      required: true,
      placeholder: 'Describe the client\'s specific needs and requirements'
    },
    {
      name: 'solutionOverview',
      label: 'Proposed Solution Overview',
      type: 'textarea',
      required: true,
      placeholder: 'Provide an overview of the proposed solution'
    },
    {
      name: 'deliverables',
      label: 'Key Deliverables',
      type: 'textarea',
      required: true,
      placeholder: 'List the key deliverables included in this proposal'
    },
    {
      name: 'timeline',
      label: 'Proposed Timeline',
      type: 'textarea',
      required: true,
      placeholder: 'Outline the proposed project timeline and milestones'
    },
    {
      name: 'pricing',
      label: 'Pricing Structure',
      type: 'textarea',
      required: true,
      placeholder: 'Describe the pricing structure and payment terms'
    },
    {
      name: 'successMetrics',
      label: 'Success Metrics & KPIs',
      type: 'textarea',
      required: true,
      placeholder: 'Define how success will be measured'
    }
  ]
};

export default function CompassEditor({ onViewChange }) {
  const { user } = useAuth();
  const { showWarning, showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeTab, setActiveTab] = useState(DOCUMENT_TYPES.ACCOUNT_BRIEF);
  const [inputFields, setInputFields] = useState({});
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [inputMode, setInputMode] = useState('search');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [view, setView] = useState('editor');
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [multiFieldEntries, setMultiFieldEntries] = useState({});
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchOverlayRef = useRef(null);
  const [overlaySearchResults, setOverlaySearchResults] = useState([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlaySearched, setOverlaySearched] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [editedDocumentContent, setEditedDocumentContent] = useState('');
  const [isUserEditing, setIsUserEditing] = useState(false);
  const editableDivRef = useRef(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [pendingCompanyData, setPendingCompanyData] = useState(null);
  const [existingCompanyInfo, setExistingCompanyInfo] = useState(null);

  // Add usage tracking
  const { checkUsage, incrementUsage } = useUsage();

  // Initialize input fields when document type changes
  useEffect(() => {
    const fields = {};
    const multiFields = {};
    DOCUMENT_FIELDS[activeTab].forEach(field => {
      if (field.type === 'multi-field') {
        // Initialize multi-field with one empty entry
        multiFields[field.name] = [{ id: Date.now(), name: '', type: '' }];
        fields[field.name] = multiFields[field.name];
      } else if (field.type === 'checkbox-group') {
        fields[field.name] = [];
      } else {
        fields[field.name] = '';
      }
    });
    setInputFields(fields);
    setMultiFieldEntries(multiFields);
    setGeneratedDocument(null);
  }, [activeTab]);

  // Timeout effect for saveStatus
  useEffect(() => {
    // If there's a save status message, set a timer to clear it
    if (saveStatus) {
    const timer = setTimeout(() => {
      setSaveStatus(null);
    }, 5000); // 5 seconds
    
    // Clean up function to clear the timer if component unmounts or saveStatus changes
    return () => clearTimeout(timer);
    }
  }, [saveStatus]); // This effect runs whenever saveStatus changes

  // Clear search results when switching to web search mode
  useEffect(() => {
    if (inputMode === 'web') {
      setOverlaySearchResults([]);
    }
  }, [inputMode]);

  // Search accounts
  useEffect(() => {
    const searchAccounts = async () => {
      // Only search database when in 'search' mode, not 'web' mode
      if (inputMode !== 'search' || searchQuery.length < 2) {
        setOverlaySearchResults([]);
        return;
      }

      setOverlayLoading(true);
      setOverlaySearched(true);
      setOverlaySearchResults([]);
      try {
        const companies = await searchCompanies(searchQuery);
        setOverlaySearchResults(companies);
      } catch (error) {
        console.error('Error searching companies:', error);
        setOverlaySearchResults([]);
      } finally {
        setOverlayLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchAccounts, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, inputMode]);

  // Add to recent searches (persisted) - user-specific
  useEffect(() => {
    // Load from localStorage on mount - user-specific
    const userKey = `recentSearches_${user?.id || 'anonymous'}`;
    const stored = localStorage.getItem(userKey);
    if (stored) {
      setRecentSearches(JSON.parse(stored));
    }
  }, [user?.id]);

  useEffect(() => {
    if (selectedAccount && selectedAccount.name) {
      setRecentSearches(prev => {
        const updated = [selectedAccount.name, ...prev.filter(name => name !== selectedAccount.name)];
        const userKey = `recentSearches_${user?.id || 'anonymous'}`;
        localStorage.setItem(userKey, JSON.stringify(updated.slice(0, 10)));
        return updated.slice(0, 10); // Keep only last 10 unique
      });
    }
  }, [selectedAccount, user?.id]);

  // Close overlay on outside click or Esc
  useEffect(() => {
    if (!showSearchOverlay) return;
    function handleClick(e) {
      if (searchOverlayRef.current && !searchOverlayRef.current.contains(e.target)) {
        setShowSearchOverlay(false);
      }
    }
    function handleEsc(e) {
      if (e.key === 'Escape') setShowSearchOverlay(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showSearchOverlay]);

  const handleMenuSelect = (option) => {
    if (option === 'Saved Notes') {
      navigate('/app/compass/saved-notes');
    } else if (option === 'editor') {
      navigate('/app/compass/editor');
    }
    setMenuOpen(false);
  };

  const handleWebsiteClick = (e, website) => {
    // Check if URL is properly formatted (starts with http:// or https://)
    if (website && !website.match(/^https?:\/\//i)) {
      e.preventDefault();
      showWarning(
        `The website URL is not properly formatted. Please check the accounts section in the Radar module and ensure the URL is in the format: https://www.example.com`,
        {
          title: 'Invalid Website URL',
          duration: 7000
        }
      );
    }
    // If URL is valid, allow default navigation
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add a helper function to set selectedAccount and searchQuery from companyInfo
  function setCompanyFromInfo(companyInfo) {
    if (!companyInfo || !companyInfo.companyName) {
      console.error('No companyName in companyInfo:', companyInfo);
      return false;
    }
    setSelectedAccount({
      name: companyInfo.companyName,
      website: companyInfo.website || '',
      summary: companyInfo.summary || '',
      industry: companyInfo.industry || '',
      companySize: companyInfo.companySize || ''
    });
    setSearchQuery(companyInfo.companyName);
    console.log('Set selectedAccount:', {
      name: companyInfo.companyName,
      website: companyInfo.website || '',
      summary: companyInfo.summary || '',
      industry: companyInfo.industry || '',
      companySize: companyInfo.companySize || ''
    });
    return true;
  }

  const handleInputChange = (field, value) => {
    setInputFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setInputFields(prev => {
      const currentValues = prev[fieldName] || [];
      if (checked) {
        return {
          ...prev,
          [fieldName]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [fieldName]: currentValues.filter(v => v !== value)
        };
      }
    });
  };

  const handleMultiFieldChange = (fieldName, entryId, subField, value) => {
    setMultiFieldEntries(prev => {
      const currentEntries = prev[fieldName] || [];
      const updatedEntries = currentEntries.map(entry => 
        entry.id === entryId 
          ? { ...entry, [subField]: value }
          : entry
      );
      
      // Update both multiFieldEntries and inputFields
      setInputFields(prevFields => ({
        ...prevFields,
        [fieldName]: updatedEntries
      }));
      
      return {
        ...prev,
        [fieldName]: updatedEntries
      };
    });
  };

  const addMultiFieldEntry = (fieldName) => {
    const newEntry = { id: Date.now(), name: '', type: '' };
    
    setMultiFieldEntries(prev => {
      const currentEntries = prev[fieldName] || [];
      const updatedEntries = [...currentEntries, newEntry];
      
      // Update both multiFieldEntries and inputFields
      setInputFields(prevFields => ({
        ...prevFields,
        [fieldName]: updatedEntries
      }));
      
      return {
        ...prev,
        [fieldName]: updatedEntries
      };
    });
  };

  const removeMultiFieldEntry = (fieldName, entryId) => {
    setMultiFieldEntries(prev => {
      const currentEntries = prev[fieldName] || [];
      // Don't remove if it's the last entry
      if (currentEntries.length <= 1) {
        return prev;
      }
      
      const updatedEntries = currentEntries.filter(entry => entry.id !== entryId);
      
      // Update both multiFieldEntries and inputFields
      setInputFields(prevFields => ({
        ...prevFields,
        [fieldName]: updatedEntries
      }));
      
      return {
        ...prev,
        [fieldName]: updatedEntries
      };
    });
  };

  const handleGenerateDocument = async (feedback = null) => {
    if (!selectedAccount) {
      showError('Please select or create an account first');
      return;
    }

    const requiredFields = DOCUMENT_FIELDS[activeTab].filter(field => field.required);
    const missingFields = requiredFields.filter(field => !inputFields[field.name]);

    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    // Set loading state immediately for better UX
    setIsGeneratingDocument(true);
    setSaveStatus(null);

    try {
      // Check usage limits after setting loading state
      const usageCheck = await checkUsage('documentCreations');
      if (!usageCheck.allowed) {
        showError(`You've reached your monthly limit of ${usageCheck.limit} document creations. Please upgrade your plan for unlimited access.`);
        return;
      }

      let document;
      console.log('Generating document with type:', activeTab);
      console.log('Input fields:', inputFields);
      if (feedback) {
        console.log('Including feedback in generation:', feedback);
      }
      
      document = await generateDocument(activeTab, selectedAccount, inputFields, feedback);
      console.log('Raw document response:', document);

      // Handle both string and object responses
      let parsedDocument;
      if (typeof document === 'string') {
        try {
          parsedDocument = JSON.parse(document);
        } catch (parseError) {
          console.error('Error parsing document string:', parseError);
          showError('Generated document is not in the expected format. Please try again.');
          return;
        }
      } else if (typeof document === 'object' && document !== null) {
        parsedDocument = document;
      } else {
        console.error('Unexpected document format:', document);
        showError('Generated document is not in the expected format. Please try again.');
        return;
      }

      console.log('Parsed document:', parsedDocument);
      setGeneratedDocument(JSON.stringify(parsedDocument, null, 2));
      
      const successMessage = feedback ? 'Document refined successfully!' : 'Document generated successfully!';
      setSaveStatus({
        type: 'success',
        message: successMessage
      });
      showSuccess(successMessage);
    } catch (error) {
      console.error('Error generating document:', error);
      showError('Failed to generate document. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const handleSaveDocument = async () => {
    if (!editedDocumentContent || !selectedAccount) {
      showError('Please generate a document and select a company first');
      return;
    }
    setIsGeneratingDocument(true);
    try {
      // Handle different document types
      if (activeTab === DOCUMENT_TYPES.PROPOSAL) {
        // Placeholder for proposal saving - will be implemented later
        setSaveStatus({
          type: 'success',
          message: 'Proposal saved successfully (placeholder)!'
        });
      } else {
        const successMessage = `${activeTab} saved successfully!`;
        setSaveStatus({
          type: 'success',
          message: successMessage
        });
        showSuccess(successMessage);
      }
      setShowSaveModal(true);
    } catch (error) {
      console.error('Error saving document:', error);
      showError(error.message || 'Failed to save document. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const handleExport = () => {
    if (!editedDocumentContent) {
      showError('No document to export');
      return;
    }
    try {
      // Export as PDF using jsPDF's html() to preserve formatting
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 48; // 48pt margin on each side
      const contentWidth = pageWidth - 2 * margin;
      const startY = margin; // Starting Y position for content (top margin)
      // Create a temporary div to hold the HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = editedDocumentContent;
      tempDiv.style.width = contentWidth + 'pt';
      tempDiv.style.margin = '0 auto';
      tempDiv.style.textAlign = 'left';
      tempDiv.style.whiteSpace = 'normal';
      tempDiv.style.fontFamily = 'Times, Times New Roman, serif';
      tempDiv.style.fontSize = '12pt';
      // Ensure all child divs are left-aligned and normal white-space
      Array.from(tempDiv.querySelectorAll('div')).forEach(div => {
        div.style.textAlign = 'left';
        div.style.whiteSpace = 'normal';
        div.style.fontFamily = 'Times, Times New Roman, serif';
        div.style.fontSize = '12pt';
      });
      document.body.appendChild(tempDiv);
      doc.html(tempDiv, {
        x: margin,
        y: startY, // Use the startY for the top margin
        width: contentWidth,
        windowWidth: contentWidth,
        callback: function (doc) {
          doc.save(`${activeTab}_${selectedAccount?.name || 'Document'}.pdf`);
          setSaveStatus({
            type: 'success',
            message: 'Document exported as PDF!'
          });
          showSuccess('Document exported as PDF!');
          document.body.removeChild(tempDiv);
        },
        autoPaging: 'text',
        html2canvas: { scale: 0.7 }
      });
    } catch (error) {
      console.error('Error exporting document:', error);
      showError('Failed to export document as PDF. Please try again.');
    }
  };

  const handleSaveToDatabase = async () => {
    if (!selectedAccount) return;

    setIsSavingCompany(true);
    setSaveStatus(null);
    
    const companyData = {
      companyName: selectedAccount.name,
      website: selectedAccount.website && selectedAccount.website.trim() ? selectedAccount.website.trim() : null,
      summary: selectedAccount.summary || '',
      status: 'active',
      source: 'manual', // Mark as manual since it's from web search
      industry: selectedAccount.industry || null,
      companySize: selectedAccount.companySize || null
    };
    
    console.log('Saving company data:', companyData);
    
    try {
      // Check if company exists first
      const existenceCheck = await checkCompanyExists(selectedAccount.name);
      
      if (existenceCheck.exists) {
        // Show custom overwrite confirmation modal
        setPendingCompanyData(companyData);
        setExistingCompanyInfo(existenceCheck);
        setShowOverwriteConfirm(true);
        setIsSavingCompany(false); // Ensure it's false before showing prompt
        return;
      }

      const savedCompany = await saveCompanyToDatabase(companyData);
      console.log('Company saved successfully:', savedCompany);

      // Update the selected account with the database ID
      setSelectedAccount({
        ...selectedAccount,
        id: savedCompany.id
      });

      const successMessage = existenceCheck.exists ? 'Company updated successfully!' : 'Company saved to database successfully!';
      setSaveStatus({
        type: 'success',
        message: successMessage
      });
      showSuccess(successMessage);
      setShowSavePrompt(false);
    } catch (error) {
      console.error('Error saving company:', error);
      console.error('Error response:', error.response?.data);
      showError(error.response?.data?.message || error.message || 'Failed to save company to database. Please try again.');
    } finally {
      setIsSavingCompany(false);
    }
  };

  const renderOverwriteConfirm = () => {
    if (!showOverwriteConfirm || !pendingCompanyData || !existingCompanyInfo) return null;

    return (
      <div className="save-prompt">
        <div className="save-prompt-content">
          <h3>Company Already Exists</h3>
          <p>
            Company <strong>"{existingCompanyInfo.company.companyName}"</strong> already exists in your database.
          </p>
          <div className="overwrite-details">
            <div className="existing-info">
              <h4>Current Information:</h4>
              <p><strong>Website:</strong> {existingCompanyInfo.company.website || 'Not specified'}</p>
              <p><strong>Summary:</strong> {existingCompanyInfo.company.summary ? 
                existingCompanyInfo.company.summary.substring(0, 100) + '...' : 'Not specified'}</p>
            </div>
            <div className="new-info">
              <h4>New Information:</h4>
              <p><strong>Website:</strong> {pendingCompanyData.website || 'Not specified'}</p>
              <p><strong>Summary:</strong> {pendingCompanyData.summary ? 
                pendingCompanyData.summary.substring(0, 100) + '...' : 'Not specified'}</p>
            </div>
          </div>
          <p>Do you want to overwrite the existing information with the new data?</p>
          <div className="save-prompt-actions">
            <button
              className="btn-save"
              onClick={handleConfirmOverwrite}
              disabled={isSavingCompany}
            >
              {isSavingCompany ? (
                <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <FaDatabase />
                  Overwrite
                </>
              )}
            </button>
            <button
              className="btn-cancel"
              onClick={handleCancelOverwrite}
              disabled={isSavingCompany}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleConfirmOverwrite = async () => {
    if (!pendingCompanyData) return;
    setIsSavingCompany(true);
    const companyDataWithOverwrite = {
      ...pendingCompanyData,
      overwrite: true
    };
    try {
      const savedCompany = await saveCompanyToDatabase(companyDataWithOverwrite);
      setSelectedAccount({
        ...selectedAccount,
        id: savedCompany.id
      });
      setSaveStatus({
        type: 'success',
        message: 'Company updated successfully!'
      });
      showSuccess('Company updated successfully!');
      setShowSavePrompt(false);
    } catch (error) {
      showError(error.response?.data?.message || error.message || 'Failed to update company. Please try again.');
    } finally {
      setIsSavingCompany(false);
      setShowOverwriteConfirm(false);
      setPendingCompanyData(null);
      setExistingCompanyInfo(null);
    }
  };

  const handleCancelOverwrite = () => {
    setShowOverwriteConfirm(false);
    setPendingCompanyData(null);
    setExistingCompanyInfo(null);
    setIsSavingCompany(false);
  };

  const renderInputFields = () => {
    return (
      <div className="input-fields">
        {DOCUMENT_FIELDS[activeTab].map(field => (
          <div key={field.name} className="input-field">
            <label className="field-label">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            {field.type === 'multi-field' ? (
              <div className="multi-field-group">
                {(multiFieldEntries[field.name] || []).map(entry => (
                  <div key={entry.id} className="multi-field-entry">
                    {field.name === 'competitors' ? (
                      <>
                        <div className="multi-field-subfield">
                          <input
                            type="text"
                            value={entry.name}
                            onChange={(e) => handleMultiFieldChange(field.name, entry.id, 'name', e.target.value)}
                            placeholder="Enter competitor name"
                            required={field.required}
                          />
                        </div>
                        <div className="multi-field-subfield">
                          <select
                            value={entry.type}
                            onChange={(e) => handleMultiFieldChange(field.name, entry.id, 'type', e.target.value)}
                            required={field.required}
                          >
                            <option value="">Select type</option>
                            {field.fields[1].options.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : field.name === 'buyerPersona' ? (
                      <>
                        <div className="multi-field-subfield">
                          <input
                            type="text"
                            value={entry.role || ''}
                            onChange={(e) => handleMultiFieldChange(field.name, entry.id, 'role', e.target.value)}
                            placeholder={field.fields[0].placeholder}
                            required={field.required}
                          />
                        </div>
                        <div className="multi-field-subfield">
                          <input
                            type="text"
                            value={entry.motivation || ''}
                            onChange={(e) => handleMultiFieldChange(field.name, entry.id, 'motivation', e.target.value)}
                            placeholder={field.fields[1].placeholder}
                            required={field.required}
                          />
                        </div>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeMultiFieldEntry(field.name, entry.id)}
                      title="Remove entry"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-add"
                  onClick={() => addMultiFieldEntry(field.name)}
                >
                  + Add {field.name === 'buyerPersona' ? 'Persona' : 'Competitor'}
                </button>
              </div>
            ) : field.type === 'select' ? (
              <select
                value={inputFields[field.name] || ''}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                required={field.required}
              >
                <option value="">Select {field.label.toLowerCase()}</option>
                {field.options.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === 'checkbox-group' ? (
              <div className="checkbox-group">
                {field.options.map(option => (
                  <label key={option} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(inputFields[field.name] || []).includes(option)}
                      onChange={(e) => handleCheckboxChange(field.name, option, e.target.checked)}
                      required={field.required && (!inputFields[field.name] || inputFields[field.name].length === 0)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            ) : field.type === 'textarea' ? (
              <textarea
                value={inputFields[field.name] || ''}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                rows={2}
                required={field.required}
              />
            ) : (
              <input
                type={field.type}
                value={inputFields[field.name] || ''}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
                required={field.required}
              />
            )}
          </div>
        ))}
        <button
          className="btn-generate-document"
          onClick={() => handleGenerateDocument()}
          disabled={isGeneratingDocument || !selectedAccount}
        >
          {isGeneratingDocument ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaFileAlt />}
          Generate Document
        </button>
      </div>
    );
  };

  const renderGeneratedDocument = () => {
    if (!editedDocumentContent) return null;
          return (
      <div
        className="document-page-text editable-doc"
        contentEditable
        suppressContentEditableWarning
        ref={editableDivRef}
        onFocus={() => setIsUserEditing(true)}
        onBlur={() => setIsUserEditing(false)}
        onInput={e => setEditedDocumentContent(e.currentTarget.innerHTML)}
        style={{ minHeight: 400, outline: 'none', whiteSpace: 'pre-wrap' }}
      />
    );
  };

  const renderSavePrompt = () => {
    if (!showSavePrompt || !selectedAccount) return null;

    return (
      <div className="save-prompt">
        <div className="save-prompt-content">
          <h3>Save Company to Database?</h3>
          <p>This company was found through web search. Would you like to save it to your database for future use?</p>
          <div className="save-prompt-actions">
            <button
              className="btn-save"
              onClick={handleSaveToDatabase}
              disabled={isSavingCompany}
            >
              {isSavingCompany ? (
                <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <FaDatabase />
                  Save to Database
                </>
              )}
            </button>
            <button
              className="btn-cancel"
              onClick={() => setShowSavePrompt(false)}
              disabled={isSavingCompany}
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper to handle overlay search
  async function handleOverlaySearch() {
    if (!searchQuery.trim()) return;
    setOverlayLoading(true);
    setOverlaySearched(true);
    setOverlaySearchResults([]);
    if (inputMode === 'search') {
      try {
        const companies = await searchCompanies(searchQuery);
        setOverlaySearchResults(companies);
      } catch (error) {
        console.error('Error searching companies:', error);
        // Ensure we're not accidentally throwing an Event object
        setOverlaySearchResults([]);
      } finally {
        setOverlayLoading(false);
      }
    } else if (inputMode === 'manual') {
      try {
        const companyInfo = await generateCompanyInfo(searchQuery);
        setOverlaySearchResults(companyInfo && companyInfo.companyName ? [companyInfo] : []);
      } catch (error) {
        console.error('Error generating company info:', error);
        // Ensure we're not accidentally throwing an Event object
        setOverlaySearchResults([]);
      } finally {
        setOverlayLoading(false);
      }
    }
  }

  // When a user clicks a result in the overlay
  function handleOverlayResultClick(result) {
    if (inputMode === 'search') {
      setCompanyFromInfo(result);
    } else if (inputMode === 'manual') {
      setCompanyFromInfo(result);
      // Show save prompt for web search results
      setShowSavePrompt(true);
    }
    setShowSearchOverlay(false);
    setOverlaySearchResults([]);
    setOverlaySearched(false);
  }

  useEffect(() => {
    if (!showSaveModal) return;
    function handleClick(e) {
      if (document.getElementById('save-modal') && !document.getElementById('save-modal').contains(e.target)) {
        setShowSaveModal(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSaveModal]);

  // Helper function to convert markdown-like text to HTML
  const formatTextToHtml = (text) => {
    if (!text) return '';
    
    // Convert markdown-style formatting
    let html = text
      // Convert **bold** to <strong>
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Convert *italic* to <em>
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Convert --- to <hr>
      .replace(/^---$/gm, '<hr>')
      // Convert markdown headings
      .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
      .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
      .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Convert - list items to <ul><li>
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Convert numbered list items
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Convert line breaks
      .replace(/\n/g, '<br>');
    
    // Wrap list items in <ul> tags
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    
    return html;
  };

  // In useEffect for generatedDocument:
  useEffect(() => {
    if (!isUserEditing && editableDivRef.current) {
      if (generatedDocument) {
        try {
          // Handle both string and object formats
          let documentData;
          if (typeof generatedDocument === 'string') {
            documentData = JSON.parse(generatedDocument);
          } else {
            documentData = generatedDocument;
          }
          
          // If document has a 'content' property, use that instead
          if (documentData.content && typeof documentData.content === 'string') {
            // Handle plain text/markdown content
            const formattedContent = formatTextToHtml(documentData.content);
            setEditedDocumentContent(formattedContent);
            editableDivRef.current.innerHTML = formattedContent;
            return;
          }
          
          // Filter out metadata and context properties
          const { metadata, context, ...contentData } = documentData;
          
          // Build HTML with section headers in bold and spacing after each section
          const documentHtml = Object.entries(contentData)
            .filter(([key]) => key !== 'metadata' && key !== 'context' && key !== 'format')
            .map(([section, content]) => {
              let sectionContent = '';
              if (typeof content === 'object' && content !== null) {
                sectionContent = Object.entries(content)
                  .map(([key, value]) => {
                    if (typeof value === 'string') {
                      // Format the text content
                      const formatted = formatTextToHtml(value);
                      return `<div style='margin-top: 0.5em;'>${formatted}</div>`;
                    } else {
                      return `<div style='margin-top: 0.5em;'>${JSON.stringify(value, null, 2)}</div>`;
                    }
                  })
                  .join('');
              } else if (typeof content === 'string') {
                // Format the text content
                const formatted = formatTextToHtml(content);
                sectionContent = `<div style='margin-top: 0.5em;'>${formatted}</div>`;
              } else {
                sectionContent = `<div style='margin-top: 0.5em;'>${JSON.stringify(content, null, 2)}</div>`;
              }
              return `<div style='margin-bottom: 1.5em; display: block;'><strong style='font-size:1.1em; display: block; margin-bottom: 0.5em;'>${section}</strong>${sectionContent}</div>`;
            })
            .join('');
          setEditedDocumentContent(documentHtml);
          // Set the content directly to the editable div
          editableDivRef.current.innerHTML = documentHtml;
        } catch {
          // If parsing fails, try to format as plain text/markdown
          const formattedContent = formatTextToHtml(generatedDocument);
          setEditedDocumentContent(formattedContent);
          editableDivRef.current.innerHTML = formattedContent;
        }
      } else {
        setEditedDocumentContent('');
        if (editableDivRef.current) {
          editableDivRef.current.innerHTML = '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedDocument]);

  // Formatting toolbar (undo/redo):
  function handleFormat(command, value = null) {
    if (editableDivRef.current) {
      editableDivRef.current.focus();
      // Ensure the editor has focus before executing commands
      const selection = window.getSelection();
      if (!selection.rangeCount) {
        const range = document.createRange();
        range.selectNodeContents(editableDivRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand(command, false, value);
    }
  }

  // Add this effect after the other useEffects for overlay/search state
  useEffect(() => {
    if (showSearchOverlay && searchQuery.trim() === '') {
      setOverlaySearched(false);
      setOverlaySearchResults([]);
      setOverlayLoading(false);
    }
  }, [searchQuery, showSearchOverlay]);

  return (
    <div style={{ background: '#F7F9FC', minHeight: '100vh', width: '100%' }}>
      <div className="compass-header-row" style={{ background: 'linear-gradient(120deg, rgba(241, 245, 249, 1) 3%, rgba(219, 234, 254, 1) 37%, rgba(237, 233, 254, 1) 100%)', height: '8vh', padding: '20px 0px 10px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', marginBottom: 24, alignItems: 'flex-start' }}>
          <div className="compass-header-left">
            <div style={{ fontSize: 24, fontWeight: 600, padding: 0, margin: 0 }}>Editor</div>
          </div>
          <div className="compass-header-right" style={{ display: 'flex', gap: 8, paddingRight: 10 }}>
            <div className="compass-hamburger-wrapper" ref={menuRef}>
              <button className="compass-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
                <FaBars />
              </button>
              {menuOpen && (
                <>
                  <div className="compass-menu-overlay" onClick={() => setMenuOpen(false)} />
                  <div className="compass-menu-dropdown">
                    <div className="compass-menu-item" onClick={() => handleMenuSelect('Saved Notes')}>Saved Notes</div>
                    <div className="compass-menu-item" onClick={() => handleMenuSelect('editor')}>Editor</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      <div className="compass-editor-container" style={{ background: 'transparent', marginTop: 0 }}>
        {view === 'editor' && (
          <div className="editor-flex-layout">
            {/* Left: Editor header + company info */}
            <div className="editor-left-pane">
              <div className="editor-header">
                <div className="search-container" onClick={() => setShowSearchOverlay(true)} style={{ cursor: 'pointer' }}>
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    placeholder="Search for an account"
                    className="search-input"
                      readOnly
                      style={{ cursor: 'pointer', border: 'none' }}
                  />
                </div>
                </div>
              </div>
            {selectedAccount && (
              <div className="company-info-card">
                <div className="company-header">
                  <h2>{selectedAccount.name}</h2>
                  {selectedAccount.website && (
                    <a 
                      href={selectedAccount.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="company-website"
                      onClick={(e) => handleWebsiteClick(e, selectedAccount.website)}
                    >
                      <FaExternalLinkAlt />
                      Visit Website
                    </a>
                  )}
                </div>
                {selectedAccount.summary && (
                  <div className="company-summary">
                    {selectedAccount.summary.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

            {/* Right: Document tabs + editor content */}
            <div className="editor-right-pane">
          {saveStatus && saveStatus.type === 'success' && (
            <div className={`save-status ${saveStatus.type}`}>
              {saveStatus.message}
            </div>
          )}
          {renderSavePrompt()}
          {renderOverwriteConfirm()}
            <div className="document-tabs">
              {Object.values(DOCUMENT_TYPES).map(tab => (
                <button
                  key={tab}
                  className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="editor-content">
              {renderInputFields()}
                  </div>
              </div>
            </div>
      )}
        {view === 'Saved Notes' && (
        <Compass 
          onViewChange={(newView) => {
            setView(newView);
            if (onViewChange) {
              onViewChange(newView);
            }
          }} 
          hideHeader={true}
          defaultView="Saved Notes"
          />
        )}

        {/* Document Overlay */}
        {generatedDocument && (
          <div className="document-overlay">
            <div className="document-overlay-header">
              <div className="document-overlay-title">
                <h2>{activeTab} - {selectedAccount?.name || 'Document'}</h2>
              </div>
              <div className="document-overlay-toolbar">
                <div className="formatting-toolbar">
                  <button className="toolbar-btn" title="Bold" onClick={() => handleFormat('bold')}><strong>B</strong></button>
                  <button className="toolbar-btn" title="Italic" onClick={() => handleFormat('italic')}><em>I</em></button>
                  <button className="toolbar-btn" title="Underline" onClick={() => handleFormat('underline')}><u>U</u></button>
                  <div className="toolbar-separator"></div>
                  <button className="toolbar-btn" title="Bullet List" onClick={() => handleFormat('insertUnorderedList')}>•</button>
                  <button className="toolbar-btn" title="Numbered List" onClick={() => handleFormat('insertOrderedList')}>1.</button>
                  <div className="toolbar-separator"></div>
                  <button className="toolbar-btn" title="Heading 1" onClick={() => handleFormat('formatBlock', 'H1')}>H1</button>
                  <button className="toolbar-btn" title="Heading 2" onClick={() => handleFormat('formatBlock', 'H2')}>H2</button>
                  <button className="toolbar-btn" title="Undo" onClick={() => handleFormat('undo')}>⎌</button>
                  <button className="toolbar-btn" title="Redo" onClick={() => handleFormat('redo')}>↻</button>
                </div>
                <div className="document-overlay-actions">
                  <button className="btn-save-overlay" onClick={handleSaveDocument} disabled={isGeneratingDocument}>
                    {isGeneratingDocument ? <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Saving</> : <><FaSave /> Save</>}
                  </button>
                  <button className="btn-export-overlay" onClick={handleExport}>
                    <FaDownload />
                    Export
                  </button>
                  <button className="btn-close-overlay" onClick={() => setGeneratedDocument(null)}>
                    ×
                  </button>
                </div>
              </div>
            </div>
            <div className="document-overlay-content">
              {renderGeneratedDocument()}
            </div>
          </div>
        )}

        {/* Search Overlay */}
        {showSearchOverlay && (
          <div className="search-overlay">
            <div className="search-overlay-content" ref={searchOverlayRef}>
              {/* Row 1: Search bar + submit button */}
              <div className="search-overlay-row search-bar-row">
                <div className="search-input-wrapper" style={{ flex: 1 }}>
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search for an account"
                    className="search-input"
                    autoFocus
                    style={{ border: 'none' }}
                    onKeyDown={e => { if (e.key === 'Enter') { handleOverlaySearch(); } }}
                  />
                </div>
                <button
                  className="search-submit-btn"
                  style={{ marginLeft: 12 }}
                  onClick={handleOverlaySearch}
                  disabled={!searchQuery.trim()}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaSearch style={{ fontSize: 18, color: 'white' }} />
                  </span>
                </button>
              </div>
              {/* Row 2/3: Either type filter + recent searches, or search results */}
              {overlaySearched ? (
                <div className="search-overlay-row recent-searches-row">
                  <span className="recent-searches-label">Search Results</span>
                  {overlayLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 48 }}>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : overlaySearchResults.length === 0 ? (
                    <span className="recent-searches-empty">No results found</span>
                  ) : (
                    <div className="recent-searches-list">
                      {overlaySearchResults.map((result, idx) => (
                        <div
                          key={result.id || result.companyName || result.name || idx}
                          className="recent-search-item"
                          onClick={() => handleOverlayResultClick(result)}
                          style={{ cursor: 'pointer' }}
                        >
                          {result.companyName || result.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="search-overlay-row search-type-row">
                    <span className="search-type-label">Type:</span>
                    <div className="search-type-options">
                      <label className={`search-type-option${inputMode === 'search' ? ' selected' : ''}`}> 
                        <input
                          type="radio"
                          checked={inputMode === 'search'}
                          onChange={() => setInputMode('search')}
                          style={{ display: 'none' }}
                        />
                        <span className="search-type-radio" /> Database Search
                      </label>
                      <label className={`search-type-option${inputMode === 'manual' ? ' selected' : ''}`}> 
                        <input
                          type="radio"
                          checked={inputMode === 'manual'}
                          onChange={() => setInputMode('manual')}
                          style={{ display: 'none' }}
                        />
                        <span className="search-type-radio" /> Web Search
                      </label>
                    </div>
                  </div>
                  <div className="search-overlay-row recent-searches-row">
                    <span className="recent-searches-label">Recent Searches</span>
                    <div className="recent-searches-list">
                      {recentSearches.length === 0 && (
                        <span className="recent-searches-empty">No recent searches</span>
                      )}
                      {recentSearches.slice(0, 5).map((name, idx) => (
                        <div
                          key={name + idx}
                          className="recent-search-item"
                          onClick={() => {
                            setSearchQuery(name);
                            handleOverlaySearch();
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {name}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showSaveModal && (
          <div className="save-modal-overlay">
            <div className="save-modal" id="save-modal">
              <h2>Document saved</h2>
              <p>Your document has been saved successfully.</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
} 